import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/apiService';

// Mirrors the QrCode type in QrCodes.tsx; kept local so this component
// doesn't import from a page file (circular).
export interface EditableQr {
  id: number;
  slug: string;
  name: string;
  destination_url: string;
  is_dynamic: boolean;
  qr_dark: string;
  data_dark: string;
  output_size_px: number;
  logo_percent: number;
  quiet_zone: number;
  black_to_white: boolean;
  png_url: string;
}

interface EditableFields {
  name: string;
  destination_url: string;
  qr_dark: string;
  data_dark: string;
  output_size_px: number;
  logo_percent: number;
  quiet_zone: number;
  black_to_white: boolean;
}

interface Props {
  qr: EditableQr;
  onClose: () => void;
  onSaved: () => void;
}

const PREVIEW_DEBOUNCE_MS = 300;

function snapshot(qr: EditableQr): EditableFields {
  return {
    name: qr.name,
    destination_url: qr.destination_url,
    qr_dark: qr.qr_dark,
    data_dark: qr.data_dark,
    output_size_px: qr.output_size_px,
    logo_percent: qr.logo_percent,
    quiet_zone: qr.quiet_zone,
    black_to_white: qr.black_to_white,
  };
}

// Fields that alter the pixels (change → preview refetch).
const VISUAL_FIELDS = [
  'qr_dark', 'data_dark', 'output_size_px', 'logo_percent', 'quiet_zone', 'black_to_white', 'destination_url',
] as const;

const QrEditModal: React.FC<Props> = ({ qr, onClose, onSaved }) => {
  const initialRef = useRef<EditableFields>(snapshot(qr));
  const [fields, setFields] = useState<EditableFields>(snapshot(qr));
  const [previewUrl, setPreviewUrl] = useState<string | null>(qr.png_url);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [palette, setPalette] = useState<string[]>([]);

  // Pull the logo palette once when the modal opens. Failures degrade to
  // an empty palette — the color pickers still work.
  useEffect(() => {
    let cancelled = false;
    apiService
      .get<{ palette: string[] }>(`/api/qrcodes/${qr.id}/palette`)
      .then((res) => {
        if (!cancelled) setPalette(res.data.palette || []);
      })
      .catch(() => {
        if (!cancelled) setPalette([]);
      });
    return () => {
      cancelled = true;
    };
  }, [qr.id]);

  const dirty = useMemo(() => {
    const init = initialRef.current;
    return (Object.keys(init) as (keyof EditableFields)[]).some((k) => init[k] !== fields[k]);
  }, [fields]);

  const visualDirty = useMemo(() => {
    const init = initialRef.current;
    return VISUAL_FIELDS.some((k) => init[k] !== fields[k]);
  }, [fields]);

  // Debounced live preview. Only re-fetches when a visual field is actually
  // dirty; name-only edits leave the preview alone.
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!visualDirty) {
      // Reverting a visual change back to initial → restore the stored png_url
      // so we're not showing a stale blob preview.
      setPreviewUrl(qr.png_url);
      return;
    }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      setPreviewLoading(true);
      setError(null);
      try {
        const body: Record<string, any> = {
          output_size_px: fields.output_size_px,
          logo_percent: fields.logo_percent,
          quiet_zone: fields.quiet_zone,
          qr_dark: fields.qr_dark,
          data_dark: fields.data_dark,
          black_to_white: fields.black_to_white,
        };
        if (!qr.is_dynamic) body.destination_url = fields.destination_url;
        const res = await apiService.post<Blob>(`/api/qrcodes/${qr.id}/preview`, body, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        setPreviewUrl((prev) => {
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || String(e));
      } finally {
        setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [fields, visualDirty, qr.id, qr.is_dynamic, qr.png_url]);

  // Revoke any lingering blob on unmount.
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback(<K extends keyof EditableFields>(key: K, value: EditableFields[K]) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, any> = { name: fields.name };
      if (qr.is_dynamic) payload.destination_url = fields.destination_url;
      else payload.destination_url = fields.destination_url;
      payload.qr_dark = fields.qr_dark;
      payload.data_dark = fields.data_dark;
      payload.output_size_px = fields.output_size_px;
      payload.logo_percent = fields.logo_percent;
      payload.quiet_zone = fields.quiet_zone;
      payload.black_to_white = fields.black_to_white;
      await apiService.patch(`/api/qrcodes/${qr.id}`, payload);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 12, color: '#5b6573' }}>Edit QR</div>
            <h2 style={{ margin: 0 }}>{qr.name}</h2>
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <div style={body}>
          <div style={previewCol}>
            <div style={previewFrame}>
              {previewUrl && <img src={previewUrl} alt="preview" style={{ width: '100%', display: 'block', borderRadius: 6 }} />}
              {previewLoading && (
                <div style={previewOverlay}>refreshing…</div>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#5b6573', marginTop: 8, textAlign: 'center' }}>
              Live preview · slug <code>/r/{qr.slug}</code> is unchanged, so already-printed QRs keep scanning.
            </div>
          </div>

          <div style={formCol}>
            <Field label="Name">
              <input value={fields.name} onChange={(e) => update('name', e.target.value)} style={input} />
            </Field>
            <Field label="Destination URL">
              <input
                value={fields.destination_url}
                onChange={(e) => update('destination_url', e.target.value)}
                disabled={!qr.is_dynamic}
                style={{ ...input, background: qr.is_dynamic ? 'white' : '#f0f1f4' }}
              />
              <div style={helpText}>
                {qr.is_dynamic
                  ? 'Dynamic — destination edit takes effect on next scan.'
                  : 'Static — URL is baked into the QR pattern; changing it re-renders the QR.'}
              </div>
            </Field>

            <div style={twoCol}>
              <Field label="QR (outer) color">
                <input type="color" value={fields.qr_dark} onChange={(e) => update('qr_dark', e.target.value)} style={colorInput} />
                <SwatchRow palette={palette} active={fields.qr_dark} onPick={(hex) => update('qr_dark', hex)} />
              </Field>
              <Field label="Data (inner) color">
                <input type="color" value={fields.data_dark} onChange={(e) => update('data_dark', e.target.value)} style={colorInput} />
                <SwatchRow palette={palette} active={fields.data_dark} onPick={(hex) => update('data_dark', hex)} />
              </Field>
            </div>

            <div style={threeCol}>
              <Field label="Output (px)">
                <input type="number" min={100} max={2000} value={fields.output_size_px} onChange={(e) => update('output_size_px', Number(e.target.value))} style={input} />
              </Field>
              <Field label="Logo %">
                <input type="number" min={5} max={35} value={fields.logo_percent} onChange={(e) => update('logo_percent', Number(e.target.value))} style={input} />
              </Field>
              <Field label="Quiet zone">
                <input type="number" min={0} max={10} value={fields.quiet_zone} onChange={(e) => update('quiet_zone', Number(e.target.value))} style={input} />
              </Field>
            </div>
            <div style={helpText}>
              Output = final PNG width/height. Logo % = logo width as a percent of output (5–35; error correction tolerates ~30%). Quiet zone = white border in QR modules.
            </div>

            <label style={checkboxRow}>
              <input type="checkbox" checked={fields.black_to_white} onChange={(e) => update('black_to_white', e.target.checked)} /> Recolor pure-black logo pixels to white
            </label>

            {error && (
              <div style={{ background: '#fde7e9', color: '#b30021', padding: 10, borderRadius: 6, marginTop: 12, fontSize: 13 }}>{error}</div>
            )}
          </div>
        </div>

        <div style={footer}>
          <div style={{ flex: 1, fontSize: 12, color: '#5b6573' }}>
            {dirty ? 'Unsaved changes' : 'No changes'}
          </div>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={handleSave} disabled={!dirty || busy} style={{ ...btnPrimary, ...(dirty && !busy ? {} : btnDisabled) }}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 13, color: '#3a4252', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

// Click-to-pick color chips from the logo palette. Hides itself if there
// are no colors to offer (no logo, or extraction returned empty).
const SwatchRow: React.FC<{ palette: string[]; active: string; onPick: (hex: string) => void }> = ({ palette, active, onPick }) => {
  if (palette.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
      {palette.map((hex) => {
        const isActive = hex.toLowerCase() === active.toLowerCase();
        return (
          <button
            key={hex}
            type="button"
            onClick={() => onPick(hex)}
            title={hex}
            aria-label={`Pick ${hex} from logo palette`}
            style={{
              width: 22, height: 22, borderRadius: 4, cursor: 'pointer',
              background: hex,
              border: isActive ? '2px solid #0f1a2b' : '1px solid #cdd3dc',
              padding: 0,
              boxShadow: isActive ? '0 0 0 2px white inset' : 'none',
            }}
          />
        );
      })}
    </div>
  );
};

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,26,43,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const modal: React.CSSProperties = {
  background: 'white', borderRadius: 10, width: '100%', maxWidth: 900, maxHeight: '90vh',
  display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,.25)',
};
const header: React.CSSProperties = {
  padding: '16px 20px', borderBottom: '1px solid #e2e5ec',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const closeBtn: React.CSSProperties = {
  background: 'none', border: 0, fontSize: 28, color: '#5b6573', cursor: 'pointer', lineHeight: 1,
};
const body: React.CSSProperties = {
  padding: 20, overflowY: 'auto', flex: 1,
  display: 'grid', gridTemplateColumns: 'minmax(200px, 260px) 1fr', gap: 20, alignItems: 'start',
};
const previewCol: React.CSSProperties = { position: 'sticky', top: 0 };
const previewFrame: React.CSSProperties = {
  position: 'relative', background: '#f6f7fb', borderRadius: 8, padding: 12,
};
const previewOverlay: React.CSSProperties = {
  position: 'absolute', inset: 0, background: 'rgba(255,255,255,.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 12, color: '#5b6573', borderRadius: 8,
};
const formCol: React.CSSProperties = { minWidth: 0 };
const footer: React.CSSProperties = {
  padding: '12px 20px', borderTop: '1px solid #e2e5ec',
  display: 'flex', gap: 10, alignItems: 'center',
};
const input: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid #cdd3dc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
};
const colorInput: React.CSSProperties = { ...input, padding: 2, height: 36 };
const twoCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const threeCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 };
const helpText: React.CSSProperties = { fontSize: 11, color: '#5b6573', marginTop: 4 };
const checkboxRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#3a4252', margin: '8px 0',
};
const btnPrimary: React.CSSProperties = {
  background: '#0f1a2b', color: 'white', border: 0, padding: '8px 14px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: 'white', color: '#3a4252', border: '1px solid #cdd3dc', padding: '8px 14px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
};
const btnDisabled: React.CSSProperties = { opacity: 0.4, cursor: 'not-allowed' };

export default QrEditModal;
